import React, { useCallback, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ApiService from '@common/services/ApiService';
import { DefaultModal } from '@common/components/default-modal';
import { DefaultButton } from '@common/components/default-button';
import { DefaultCheckbox } from '@common/components/default-checkbox';
import WaveTextArea from '@common/components/form/WaveTextArea';
import { withStyles } from 'tss-react/mui';

const styles = (theme) => ({
  button: {
    marginTop: theme.typography.pxToRem(15)
  }
});

const RejectModal = ({
  classes,
  isOpened,
  onClose,
  onRefreshRequest,
  registration
}) => {
  const api = new ApiService();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const initialData = {
    reason: '',
    notify_user: true
  };
  const [data, setData] = useState(initialData);

  const handleInputChange = useCallback(
    (event) => {
      const { value, name } = event.target;

      setData({ ...data, [name]: value });
    },
    [data, setData]
  );

  const errorStatus = useCallback(
    (field) => {
      return validationErrors && validationErrors.hasOwnProperty(field);
    },
    [validationErrors]
  );

  const errorHelpers = useCallback(
    (field) => {
      return errorStatus(field) ? validationErrors[field][0] : null;
    },
    [errorStatus, validationErrors]
  );

  const makeFieldProps = useCallback(
    (field, fieldValue, errorField) => {
      return {
        error: errorStatus(errorField || field),
        helperText: errorHelpers(errorField || field),
        name: field,
        value: fieldValue
      };
    },
    [errorStatus, errorHelpers]
  );

  const setErrors = useCallback(
    (json) => {
      if (json.code == 1) {
        setErrorMessage(json.message);
      } else if (json.code == 2) {
        setValidationErrors(json.messages);
      }
    },
    [setErrorMessage, setValidationErrors]
  );

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);

    try {
      const url = `/api/v2/registrations/${registration.id}/reject`;
      const res = await api.put(url, data);

      if (res.status === 204) {
        handleClose();
        onRefreshRequest();
      } else if (res.status === 422 || res.status === 400) {
        setErrors(res.json);
      } else {
        console.error('Unknown response', res);
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [data, registration]);

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    setValidationErrors([]);
    setData(initialData);
    onClose(undefined);
  }, [setErrorMessage, setValidationErrors, onClose]);

  return (
    <DefaultModal
      isOpened={isOpened}
      isLoading={isLoading}
      onClose={handleClose}
      title="Incomplete registration"
      action={
        <DefaultButton
          className={classes.button}
          color="secondary"
          onClick={handleSubmit}
          size="small"
          variant="contained"
        >
          Reject
        </DefaultButton>
      }
    >
      <Grid container>
        <Grid size={12}>
          <Typography color="error">{errorMessage}</Typography>
        </Grid>
        <Grid size={12}>
          <WaveTextArea
            label="Rejection Reason"
            placeholder="Enter reason here"
            maxLength={1000}
            rows={4}
            onChange={handleInputChange}
            {...makeFieldProps('reason', data.reason)}
          />
        </Grid>
        <Grid size={12}>
          <DefaultCheckbox
            name="notify_user"
            label="Notify the user by email"
            onChange={handleInputChange}
            checkboxValue={true}
            unCheckValue={false}
            value={data.notify_user}
          />
        </Grid>
      </Grid>
    </DefaultModal>
  );
};

export default withStyles(RejectModal, styles, { withTheme: true });
